import AddIcon from "@mui/icons-material/Add";
import { Alert } from "@mui/material";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { DataSourceSelector } from "@/shared/components/visualization/controls/DataSourceSelector";
import { canAddToLogPanel } from "@/shared/components/visualization/DndContainer/dropHandlers";
import { isViewableAsConsoleLog } from "@/shared/components/visualization/Panel/predicates";
import {
  actions,
  LogPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";

import styles from "./AddColumn.module.css";
import { useIsDuplicateColumn } from "./useIsDuplicateColumn";

interface AddColumnProps {
  state: LogPanelState;
}

export function AddColumn({ state }: AddColumnProps) {
  const vizStateDispatch = useVizDispatch();
  const [isAddingNewColumn, setIsAddingNewColumn] = React.useState(true);
  const [addError, setAddError] = React.useState<string | null>(null);

  const isExistingColumn = useIsDuplicateColumn(state.data);

  const onAddColumn = () => {
    setIsAddingNewColumn(true);
  };

  const handleCancel = () => {
    setAddError(null);
    setIsAddingNewColumn(false);
  };

  if (isAddingNewColumn) {
    return (
      <div className={styles.formContainer}>
        <RobotoTypography variant="overline">New column</RobotoTypography>
        <div className={styles.form}>
          <DataSourceSelector
            onChange={(messagePathNode) => {
              // Use the same gate as drag-and-drop.
              const validation = canAddToLogPanel(state, messagePathNode);
              if (!validation.isValid) {
                setAddError(validation.errors.join(" "));
                return;
              }
              setAddError(null);
              vizStateDispatch(
                actions.addDataToLogPanel(
                  state.id,
                  messagePathNode.toTopicData(),
                ),
              );
              setIsAddingNewColumn(false);
            }}
            optionFilter={(messagePathNode) => {
              if (isExistingColumn(messagePathNode.toTopicData())) {
                return false;
              }
              return isViewableAsConsoleLog(messagePathNode);
            }}
          />

          {addError !== null && <Alert severity="error">{addError}</Alert>}

          <div className={styles.positionRight}>
            <RobotoButton
              eventName={"AddColumnCanceled"}
              onClick={handleCancel}
              size="small"
              type="button"
            >
              Cancel
            </RobotoButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RobotoButton
      eventName={"AddColumnClicked"}
      onClick={onAddColumn}
      size="small"
      startIcon={<AddIcon />}
    >
      Add Column
    </RobotoButton>
  );
}
